import React, { useEffect, useState } from 'react';
import { CalendarIcon, UsersIcon, CheckCircleIcon, ExternalLinkIcon, MailIcon, ClockIcon, MessageSquareIcon, ThumbsUpIcon, ChevronDownIcon, SearchIcon, InfoIcon } from 'lucide-react';
export function AnimatedFeatureShowcase() {
  const [currentView, setCurrentView] = useState(0);
  const [transitioning, setTransitioning] = useState(false);
  const totalViews = 3;
  useEffect(() => {
    const interval = setInterval(() => {
      setTransitioning(true);
      setTimeout(() => {
        setCurrentView(prev => (prev + 1) % totalViews);
        setTransitioning(false);
      }, 500);
    }, 4000);
    return () => clearInterval(interval);
  }, []);
  return <div className="relative w-full max-w-4xl mx-auto" data-id="element-40">
      {/* Futuristic frame with glow effect */}
      <div className="relative rounded-2xl overflow-hidden shadow-[0_0_30px_rgba(200,241,105,0.3)] border-2 border-[#C8F169] border-opacity-30" data-id="element-41">
        {/* Animated top bar with futuristic elements */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#3B82F6] via-[#C8F169] to-[#EC4899] z-20" data-id="element-42"></div>
        {/* Animated scan line effect */}
        <div className="absolute top-0 left-0 right-0 bottom-0 pointer-events-none z-10 overflow-hidden" data-id="element-43">
          <div className="h-px bg-[#C8F169] bg-opacity-20 w-full absolute animate-scanline" data-id="element-44"></div>
        </div>
        {/* Content container */}
        <div className="relative h-[600px] overflow-hidden bg-[#1A2331]" data-id="element-45">
          {/* Onboarding View */}
          <div className={`absolute inset-0 transition-all duration-500 ease-in-out ${currentView === 0 ? 'translate-x-0 opacity-100' : currentView === 1 ? '-translate-x-full opacity-0' : 'translate-x-full opacity-0'}`} data-id="element-46">
            <div className="h-full flex flex-col bg-[#1E2A38] p-6" data-id="element-47">
              <div className="text-center mb-8" data-id="element-48">
                <h3 className="text-2xl font-bold mb-2" data-id="element-49">Welcome to Prepz.io</h3>
                <p className="text-[#F5F7FA] text-opacity-70" data-id="element-50">
                  Let's set up your account
                </p>
              </div>
              <div className="flex-1 flex flex-col items-center justify-center" data-id="element-51">
                <div className="w-full max-w-md mx-auto bg-[#1A2331] rounded-xl p-6 border border-[#F5F7FA] border-opacity-10" data-id="element-52">
                  <h4 className="text-xl font-semibold mb-6" data-id="element-53">
                    Connect your calendar
                  </h4>
                  <div className="space-y-4 mb-8" data-id="element-54">
                    <div className="bg-[#F5F7FA] bg-opacity-5 p-4 rounded-lg border border-[#F5F7FA] border-opacity-10 flex items-center" data-id="element-55">
                      <div className="w-10 h-10 rounded-lg bg-[#C8F169] bg-opacity-20 flex items-center justify-center mr-4" data-id="element-56">
                        <CalendarIcon className="h-5 w-5 text-[#C8F169]" data-id="element-57" />
                      </div>
                      <div className="flex-1" data-id="element-58">
                        <h5 className="font-medium" data-id="element-59">Google Calendar</h5>
                        <p className="text-sm text-[#F5F7FA] text-opacity-70" data-id="element-60">
                          Connect your Google Calendar
                        </p>
                      </div>
                      <button className="bg-[#C8F169] text-[#1E2A38] px-4 py-2 rounded-lg text-sm font-medium" data-id="element-61">
                        Connect
                      </button>
                    </div>
                    <div className="bg-[#F5F7FA] bg-opacity-5 p-4 rounded-lg border border-[#F5F7FA] border-opacity-10 flex items-center" data-id="element-62">
                      <div className="w-10 h-10 rounded-lg bg-[#F5F7FA] bg-opacity-10 flex items-center justify-center mr-4" data-id="element-63">
                        <CalendarIcon className="h-5 w-5 text-[#F5F7FA] text-opacity-70" data-id="element-64" />
                      </div>
                      <div className="flex-1" data-id="element-65">
                        <h5 className="font-medium" data-id="element-66">Microsoft Outlook</h5>
                        <p className="text-sm text-[#F5F7FA] text-opacity-70" data-id="element-67">
                          Connect your Outlook Calendar
                        </p>
                      </div>
                      <button className="bg-[#F5F7FA] bg-opacity-10 text-[#F5F7FA] px-4 py-2 rounded-lg text-sm font-medium" data-id="element-68">
                        Connect
                      </button>
                    </div>
                  </div>
                  <div className="text-center" data-id="element-69">
                    <p className="text-sm text-[#F5F7FA] text-opacity-50 mb-4" data-id="element-70">
                      We'll use this to automatically prepare for your upcoming
                      meetings
                    </p>
                    <div className="inline-flex items-center space-x-2" data-id="element-71">
                      <div className="w-2 h-2 rounded-full bg-[#C8F169]" data-id="element-72"></div>
                      <div className="w-2 h-2 rounded-full bg-[#F5F7FA] bg-opacity-30" data-id="element-73"></div>
                      <div className="w-2 h-2 rounded-full bg-[#F5F7FA] bg-opacity-30" data-id="element-74"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Meeting Brief View */}
          <div className={`absolute inset-0 transition-all duration-500 ease-in-out ${currentView === 1 ? 'translate-x-0 opacity-100' : currentView === 0 ? 'translate-x-full opacity-0' : '-translate-x-full opacity-0'}`} data-id="element-75">
            <div className="h-full bg-[#1E2A38] p-6 overflow-y-auto" data-id="element-76">
              <div className="bg-[#1A2331] rounded-xl overflow-hidden shadow-lg border border-[#F5F7FA] border-opacity-10" data-id="element-77">
                <div className="p-6 border-b border-[#F5F7FA] border-opacity-10" data-id="element-78">
                  <div className="flex items-center justify-between mb-6" data-id="element-79">
                    <div className="flex items-center space-x-4" data-id="element-80">
                      <div className="w-10 h-10 rounded-full bg-[#C8F169] flex items-center justify-center text-[#1E2A38] font-bold" data-id="element-81">
                        WS
                      </div>
                      <div data-id="element-82">
                        <h3 className="font-medium" data-id="element-83">
                          Quarterly Strategy Meeting
                        </h3>
                        <p className="text-sm text-[#F5F7FA] text-opacity-60" data-id="element-84">
                          Tomorrow, 10:00 AM - 11:30 AM
                        </p>
                      </div>
                    </div>
                    <div className="bg-[#C8F169] bg-opacity-20 px-3 py-1 rounded-full" data-id="element-85">
                      <span className="text-sm text-[#C8F169]" data-id="element-86">Important</span>
                    </div>
                  </div>
                  <div data-id="element-87">
                    <h4 className="text-sm text-[#F5F7FA] text-opacity-60 mb-2" data-id="element-88">
                      Meeting Brief
                    </h4>
                    <p className="text-[#F5F7FA]" data-id="element-89">
                      This quarterly strategy meeting will focus on reviewing Q3
                      results and planning for Q4 initiatives. The team will
                      discuss current project status, resource allocation, and
                      upcoming market opportunities.
                    </p>
                  </div>
                </div>
                <div className="p-6 border-b border-[#F5F7FA] border-opacity-10" data-id="element-90">
                  <h4 className="text-sm text-[#F5F7FA] text-opacity-60 mb-4" data-id="element-91">
                    Attendees (5)
                  </h4>
                  <div className="space-y-3" data-id="element-92">
                    <div className="bg-[#1E2A38] p-3 rounded-lg" data-id="element-93">
                      <div className="flex items-center justify-between" data-id="element-94">
                        <div className="flex items-center space-x-3" data-id="element-95">
                          <div className="w-8 h-8 rounded-full bg-[#C8F169] flex items-center justify-center text-[#1E2A38] text-xs font-bold" data-id="element-96">
                            WS
                          </div>
                          <div data-id="element-97">
                            <div className="font-medium" data-id="element-98">William Smith</div>
                            <div className="text-sm text-[#F5F7FA] text-opacity-60" data-id="element-99">
                              VP of Product, TechCorp
                            </div>
                          </div>
                        </div>
                        <a href="#" className="text-[#C8F169] flex items-center hover:underline" data-id="element-100">
                          <ExternalLinkIcon className="h-4 w-4 mr-1" data-id="element-101" />
                          <span className="text-sm" data-id="element-102">LinkedIn</span>
                        </a>
                      </div>
                    </div>
                    <div className="bg-[#1E2A38] p-3 rounded-lg" data-id="element-103">
                      <div className="flex items-center justify-between" data-id="element-104">
                        <div className="flex items-center space-x-3" data-id="element-105">
                          <div className="w-8 h-8 rounded-full bg-[#C8F169] flex items-center justify-center text-[#1E2A38] text-xs font-bold" data-id="element-106">
                            AJ
                          </div>
                          <div data-id="element-107">
                            <div className="font-medium" data-id="element-108">Alice Johnson</div>
                            <div className="text-sm text-[#F5F7FA] text-opacity-60" data-id="element-109">
                              Director of Marketing, TechCorp
                            </div>
                          </div>
                        </div>
                        <a href="#" className="text-[#C8F169] flex items-center hover:underline" data-id="element-110">
                          <ExternalLinkIcon className="h-4 w-4 mr-1" data-id="element-111" />
                          <span className="text-sm" data-id="element-112">LinkedIn</span>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="p-6" data-id="element-113">
                  <h4 className="text-sm text-[#F5F7FA] text-opacity-60 mb-4" data-id="element-114">
                    Business Insights
                  </h4>
                  <div className="space-y-3" data-id="element-115">
                    <div className="flex items-start space-x-2" data-id="element-116">
                      <CheckCircleIcon className="h-5 w-5 text-[#C8F169] mt-0.5" data-id="element-117" />
                      <p className="text-[#F5F7FA]" data-id="element-118">
                        Company grew 27% in Q3, exceeding the market average of
                        18%
                      </p>
                    </div>
                    <div className="flex items-start space-x-2" data-id="element-119">
                      <CheckCircleIcon className="h-5 w-5 text-[#C8F169] mt-0.5" data-id="element-120" />
                      <p className="text-[#F5F7FA]" data-id="element-121">
                        New product launch scheduled for early Q4 with 85%
                        readiness
                      </p>
                    </div>
                    <div className="flex items-start space-x-2" data-id="element-122">
                      <CheckCircleIcon className="h-5 w-5 text-[#C8F169] mt-0.5" data-id="element-123" />
                      <p className="text-[#F5F7FA]" data-id="element-124">
                        Customer retention improved by 12% following the last
                        initiative
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Slack Message View */}
          <div className={`absolute inset-0 transition-all duration-500 ease-in-out ${currentView === 2 ? 'translate-x-0 opacity-100' : currentView === 1 ? 'translate-x-full opacity-0' : '-translate-x-full opacity-0'}`} data-id="element-125">
            <div className="h-full bg-white overflow-y-auto" data-id="element-126">
              {/* Slack Header */}
              <div className="bg-[#350D36] text-white px-4 py-2 flex items-center justify-between" data-id="element-127">
                <div className="flex items-center" data-id="element-128">
                  <span className="font-bold" data-id="element-129">Prepz Workspace</span>
                  <ChevronDownIcon className="h-4 w-4 ml-1" data-id="element-130" />
                </div>
                <div className="relative" data-id="element-131">
                  <input type="text" placeholder="Search Prepz" className="bg-[#5D3D5E] text-white rounded px-3 py-1 text-sm w-64 focus:outline-none" data-id="element-132" />
                  <SearchIcon className="absolute right-3 top-1.5 h-4 w-4 text-[#BDB6BE]" data-id="element-133" />
                </div>
              </div>
              {/* Channel Header */}
              <div className="bg-[#3F0E40] text-white px-4 py-2 flex items-center justify-between" data-id="element-134">
                <div className="flex items-center" data-id="element-135">
                  <div className="h-4 w-4 mr-2 text-[#BDB6BE]" data-id="element-136" />
                  <span className="font-bold" data-id="element-137">meeting-prep</span>
                  <InfoIcon className="h-4 w-4 ml-2 text-[#BDB6BE]" data-id="element-138" />
                </div>
              </div>
              {/* Messages Area */}
              <div className="bg-[#1A1D21] flex-1 overflow-y-auto flex flex-col p-4" data-id="element-139">
                {/* Date Divider */}
                <div className="flex items-center justify-center my-4" data-id="element-140">
                  <div className="h-px bg-[#424242] flex-grow max-w-xs" data-id="element-141"></div>
                  <span className="px-4 text-[#9B9B9B] text-xs" data-id="element-142">Today</span>
                  <div className="h-px bg-[#424242] flex-grow max-w-xs" data-id="element-143"></div>
                </div>
                {/* Prepz Bot Message */}
                <div className="px-4 py-2 hover:bg-[#222529]" data-id="element-144">
                  <div className="flex items-start mb-1" data-id="element-145">
                    <div className="w-10 h-10 rounded mr-3 overflow-hidden flex-shrink-0" data-id="element-146">
                      <div className="w-full h-full bg-[#C8F169] flex items-center justify-center text-[#1E2A38] font-bold" data-id="element-147">
                        PB
                      </div>
                    </div>
                    <div data-id="element-148">
                      <div className="flex items-center" data-id="element-149">
                        <span className="font-bold text-white" data-id="element-150">Prepz Bot</span>
                        <span className="text-[#9B9B9B] text-xs ml-2" data-id="element-151">
                          9:32 AM
                        </span>
                      </div>
                      <div className="mt-1 text-white" data-id="element-152">
                        <p className="mb-2" data-id="element-153">
                          Good morning! Here's your meeting brief for today:
                        </p>
                        {/* Meeting Brief Card */}
                        <div className="border-l-4 border-[#C8F169] bg-[#222529] rounded-r rounded-bl p-4 my-2" data-id="element-154">
                          <div className="flex items-center justify-between mb-4" data-id="element-155">
                            <div className="flex items-center" data-id="element-156">
                              <CalendarIcon className="h-5 w-5 text-[#C8F169] mr-2" data-id="element-157" />
                              <div data-id="element-158">
                                <h3 className="font-medium text-white" data-id="element-159">
                                  Quarterly Strategy Meeting
                                </h3>
                                <div className="flex items-center text-[#9B9B9B] text-sm" data-id="element-160">
                                  <ClockIcon className="h-3.5 w-3.5 mr-1" data-id="element-161" />
                                  Today, 10:00 AM - 11:30 AM
                                </div>
                              </div>
                            </div>
                            <div className="bg-[#2C3E50] px-3 py-1 rounded text-xs text-[#C8F169]" data-id="element-162">
                              Important
                            </div>
                          </div>
                          <div className="mb-4" data-id="element-163">
                            <h4 className="text-[#9B9B9B] text-sm mb-1" data-id="element-164">
                              Meeting Brief
                            </h4>
                            <p className="text-[#E2E2E2]" data-id="element-165">
                              This quarterly strategy meeting will focus on
                              reviewing Q3 results and planning for Q4
                              initiatives.
                            </p>
                          </div>
                          <div className="mb-4" data-id="element-166">
                            <h4 className="text-[#9B9B9B] text-sm mb-2" data-id="element-167">
                              Attendees (5)
                            </h4>
                            <div className="space-y-2" data-id="element-168">
                              <div className="flex items-center justify-between bg-[#2C2C2C] p-2 rounded" data-id="element-169">
                                <div className="flex items-center" data-id="element-170">
                                  <div className="w-8 h-8 rounded-full bg-[#C8F169] flex items-center justify-center text-[#1E2A38] text-xs font-bold mr-2" data-id="element-171">
                                    WS
                                  </div>
                                  <div data-id="element-172">
                                    <div className="text-white text-sm" data-id="element-173">
                                      William Smith
                                    </div>
                                    <div className="text-[#9B9B9B] text-xs" data-id="element-174">
                                      VP of Product, TechCorp
                                    </div>
                                  </div>
                                </div>
                                <a href="#" className="text-[#3B82F6] text-xs flex items-center" data-id="element-175">
                                  <ExternalLinkIcon className="h-3 w-3 mr-1" data-id="element-176" />
                                  LinkedIn
                                </a>
                              </div>
                            </div>
                          </div>
                          <div className="flex justify-end" data-id="element-177">
                            <a href="#" className="bg-[#3B82F6] hover:bg-[#2D6BCD] text-white px-4 py-1.5 rounded text-sm" data-id="element-178">
                              View Full Brief
                            </a>
                          </div>
                        </div>
                        <p data-id="element-179">
                          Need any specific information about this meeting? Just
                          ask!
                        </p>
                      </div>
                    </div>
                  </div>
                  {/* Message Actions */}
                  <div className="ml-12 mt-2 flex items-center space-x-3 text-[#9B9B9B]" data-id="element-180">
                    <button className="flex items-center text-xs hover:text-white" data-id="element-181">
                      <ThumbsUpIcon className="h-3.5 w-3.5 mr-1" data-id="element-182" />
                      <span data-id="element-183">1</span>
                    </button>
                    <button className="flex items-center text-xs hover:text-white" data-id="element-184">
                      <MessageSquareIcon className="h-3.5 w-3.5 mr-1" data-id="element-185" />
                      <span data-id="element-186">Reply</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Animated dots showing current view */}
          <div className="absolute bottom-4 left-0 right-0 flex justify-center space-x-2 z-20" data-id="element-187">
            {Array.from({
            length: totalViews
          }).map((_, index) => <div key={index} className={`w-2 h-2 rounded-full transition-all duration-300 ${currentView === index ? 'bg-[#C8F169] w-6' : 'bg-[#F5F7FA] bg-opacity-30'}`} data-id="element-188"></div>)}
          </div>
          {/* Futuristic labels for each view */}
          <div className="absolute top-4 left-4 z-20 bg-[#1A2331] bg-opacity-80 backdrop-blur-sm rounded-lg px-3 py-1 text-xs font-mono text-[#C8F169] border border-[#C8F169] border-opacity-30" data-id="element-189">
            {currentView === 0 && '01 // ONBOARDING'}
            {currentView === 1 && '02 // MEETING BRIEF'}
            {currentView === 2 && '03 // SLACK INTEGRATION'}
          </div>
        </div>
      </div>
      {/* Animated glowing elements */}
      <div className="absolute -top-10 -left-10 w-20 h-20 bg-[#C8F169] rounded-full filter blur-3xl opacity-10 animate-pulse" data-id="element-190"></div>
      <div className="absolute -bottom-10 -right-10 w-20 h-20 bg-[#3B82F6] rounded-full filter blur-3xl opacity-10 animate-pulse delay-1000" data-id="element-191"></div>
    </div>;
}